import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'

const CollaborationContext = createContext()

export function useCollaboration() {
  const context = useContext(CollaborationContext)
  if (!context) {
    throw new Error('useCollaboration must be used within CollaborationProvider')
  }
  return context
}

export function CollaborationProvider({ children }) {
  const [currentProjectId, setCurrentProjectId] = useState(null)
  const currentProjectIdRef = useRef(null)  // stable ref for use in callbacks
  const presenceIntervalRef = useRef(null)
  const [bufferedShares, setBufferedShares] = useState([])

  // Convex mutations
  const shareProjectMutation = useMutation(api.shares.shareProject)
  const removeShareMutation = useMutation(api.shares.removeShare)
  const updateShareRoleMutation = useMutation(api.shares.updateShareRole)
  const updatePresenceMutation = useMutation(api.presence.updatePresence)
  const removePresenceMutation = useMutation(api.presence.removePresence)
  const resolveUserByEmailAction = useAction(api.shares.resolveUserByEmail)

  // Reactive queries — automatically update when data changes
  const sharesQuery = useQuery(
    api.shares.listShares,
    currentProjectId ? { projectId: currentProjectId } : "skip"
  )

  // Buffer shares to prevent flicker: only update when we get real data,
  // never flash empty during Convex query re-evaluation
  useEffect(() => {
    if (sharesQuery !== undefined) {
      setBufferedShares(sharesQuery)
    }
  }, [sharesQuery])

  // Clear buffered shares when switching projects
  useEffect(() => {
    if (!currentProjectId) {
      setBufferedShares([])
    }
  }, [currentProjectId])

  const shares = bufferedShares

  const activeUsers = useQuery(
    api.presence.getActiveUsers,
    currentProjectId ? { projectId: currentProjectId } : "skip"
  ) || []

  // Fetch shares for a project (sets currentProjectId to enable reactive query)
  const fetchShares = useCallback((projectId) => {
    setCurrentProjectId(projectId)
  }, [])

  // Share project with user by email — resolves email to Clerk userId first
  const shareProject = useCallback(async (projectId, email, role = 'VIEWER') => {
    try {
      const resolved = await resolveUserByEmailAction({ email })
      await shareProjectMutation({
        projectId,
        userId: resolved.userId,
        userEmail: resolved.email,
        userName: resolved.name || undefined,
        role,
      })
      return { success: true }
    } catch (error) {
      console.error('Failed to share project:', error)
      return { success: false, error: error.message }
    }
  }, [resolveUserByEmailAction, shareProjectMutation])

  // Remove user access
  const removeShare = useCallback(async (projectId, userId) => {
    try {
      await removeShareMutation({ projectId, userId })
      return { success: true }
    } catch (error) {
      console.error('Failed to remove share:', error)
      return { success: false, error: error.message }
    }
  }, [removeShareMutation])

  // Update share role
  const updateShareRole = useCallback(async (projectId, userId, role) => {
    try {
      await updateShareRoleMutation({ projectId, userId, role })
      return { success: true }
    } catch (error) {
      console.error('Failed to update role:', error)
      return { success: false, error: error.message }
    }
  }, [updateShareRoleMutation])

  // Start presence tracking for a project
  const startPresence = useCallback((projectId) => {
    currentProjectIdRef.current = projectId
    setCurrentProjectId(projectId)

    // Initial presence update
    updatePresenceMutation({ projectId }).catch(console.error)

    // Send heartbeat every 30 seconds
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current)
    }

    presenceIntervalRef.current = setInterval(() => {
      updatePresenceMutation({ projectId }).catch(console.error)
    }, 30000)
  }, [updatePresenceMutation])

  // Stop presence tracking — uses ref so this callback stays stable
  const stopPresence = useCallback(async (projectId) => {
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current)
      presenceIntervalRef.current = null
    }

    const id = projectId || currentProjectIdRef.current
    if (id) {
      try {
        await removePresenceMutation({ projectId: id })
      } catch (error) {
        console.error('Failed to remove presence:', error)
      }
    }

    currentProjectIdRef.current = null
    setCurrentProjectId(null)
  }, [removePresenceMutation])  // no longer depends on currentProjectId state

  // Update cursor position
  const updatePresence = useCallback(async (projectId, cursorPosition = null) => {
    try {
      await updatePresenceMutation({
        projectId,
        cursorPosition: cursorPosition ? JSON.stringify(cursorPosition) : undefined,
      })
    } catch (error) {
      console.error('Failed to update presence:', error)
    }
  }, [updatePresenceMutation])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (presenceIntervalRef.current) {
        clearInterval(presenceIntervalRef.current)
      }
    }
  }, [])

  const value = {
    shares,
    activeUsers,
    fetchShares,
    shareProject,
    removeShare,
    updateShareRole,
    startPresence,
    stopPresence,
    updatePresence
  }

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  )
}
